<script setup lang="ts">
    import { useForm } from '@inertiajs/vue3';
    import TaskForm from './TaskForm.vue';
    import DialogForm from '@/components/DialogForm.vue';
    import QueryKey from '@/enums/QueryKey';
    import type { Company, SharedData, Task } from '@/types';
    import { Button } from '@/components/ui/button';
    import { DialogFooter, DialogClose } from '@/components/ui/dialog';
    import { useI18n } from 'vue-i18n';
    import Permission from '@/enums/Permission';
    import { usePermissions } from '@/composables/usePermissions';
    import { usePage } from '@inertiajs/vue3';
    import { computed, ref } from 'vue';
    import DeleteTask from './DeleteTask.vue';
    import ContinueButton from '@/components/ContinueButton.vue';

    const props = defineProps<{
        task: Task;
    }>();

    const page = usePage<SharedData>();
    const isClient = computed(() => page.props.auth.user.is_client);

    const { t } = useI18n();
    const { hasPermission } = usePermissions();

    const form = useForm({
        task_id: props.task.task_id,
        company_id: props.task.company_id,
        task_title: props.task.task_title,
        task_description: props.task.task_description,
        deadline: props.task.deadline,
        weekdays: props.task.weekdaysData,
        task_status: props.task.status,
        send_notification: props.task.send_notification,
        notification_recurring_week: props.task.notification_recurring_week,
        notification_recurring_even_week: props.task.notification_recurring_even_week,
        notification_recurring_start_of_month: props.task.notification_recurring_start_of_month,
        responsible_ids: props.task.responsible_ids,
        filename: null,
    });

    const dialogFormRef = ref<InstanceType<typeof DialogForm>>();

    const handleDelete = () => {
        dialogFormRef.value?.invalidateQueryAndClose();
    };
</script>

<template>
    <DialogForm
        ref="dialogFormRef"
        type="update"
        method="post"
        :form="form"
        :route="route('v2.dashboard.tasks.update', { task_id: props.task.task_id })"
        :query-key="[QueryKey.DASHBOARD_TASKS, props.task.company_id]"
        title="dashboards.tasks_add"
    >
        <template
            v-if="isClient"
            #trigger
        >
            <ContinueButton>
                {{ $t('forms.open') }}
            </ContinueButton>
        </template>

        <TaskForm :form="form" />

        <template #footer>
            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DeleteTask
                    v-if="hasPermission(Permission.DASHBOARD_ADMIN_DELETE)"
                    :task="task"
                    @deleted="handleDelete"
                />

                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="form.resetAndClearErrors()"
                    >
                        {{ $t('forms.cancel') }}
                    </Button>
                </DialogClose>

                <Button
                    v-if="hasPermission(Permission.DASHBOARD_UPDATE)"
                    :disabled="form.processing"
                    :loading="form.processing"
                    size="sm"
                    variant="default"
                >
                    {{ t('button-text.save') }}
                </Button>
            </DialogFooter>
        </template>
    </DialogForm>
</template>
